import * as React from 'react';
import * as Images from 'pos/Images';
import { Result, Localize } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Permission, OrderedItemsStateDTO, Res } from 'core/api';
import * as SalesStore from 'pos/modules/main/SalesStore';
import { OrderingFacade } from 'pos/facades';
import { Command } from 'pos/common';
import { Cols, Col, Flex } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';
import { ActionButtonDefinition, PosStatelessScreenClassComponent, ScreenSize } from 'pos/ui';

type Props = { status: OrderedItemsStateDTO[] } & Modal.Actions<number>;

class GetPreparationOrder extends PosStatelessScreenClassComponent<Props> {

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	protected useKeypad() {
		return false;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getScreenId(): string {
		return 'GET_PREPARATION_ORDER';
	}

	protected getTitle(): string {
		return Res.Web.REQUEST_ORDERS();
	}

	protected getImage(): string | SvgIcon {
		return Images.RequestCourses;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			null
		];
	}

	private onSelectOrder = (orderId: number) => this.close(orderId);

	protected getContent(): React.ReactNode {
		const orderList = this.props.status.map(s => {
			return <li key={s.PreparationOrderId} onClick={() => this.onSelectOrder(s.PreparationOrderId)}>
				<Cols>
					<Col className='text-left' width='*'>
						{s.Name}
					</Col>
					<Col className='text-center' width='3em'>
						{s.HasBeenRequested ? Localize.text('YES_BTN') : Localize.text('NO_BTN')}
					</Col>
				</Cols>
			</li>;
		});
		return <Flex.Vertical>
			<Flex.Item>
				<Cols className='list-header'>
					<Col width='*'>
						{Res.Web.PREPARATION_ORDER_CLM()}
					</Col>
					<Col className='text-right'>
						{Res.Web.REQUESTED_ORDER_CLM()}
					</Col>
				</Cols>
			</Flex.Item>
			<Flex.Item size='*'>
				<ul className='list'>
					{orderList}
				</ul>
			</Flex.Item>
		</Flex.Vertical>;
	}
}

async function confirmRequestAgain(state: OrderedItemsStateDTO): Promise<boolean> {
	const result = await Message.confirm(Res.Msg.MSG_CONFIRM_REQUEST_ITEMS_AGAIN(state.Name));
	return result.ok;
}

async function getOrder(ticketIds: number[]): Promise<Result<number>> {

	const orderedItemsStates = (await OrderingFacade.getOrderedItemsState(ticketIds))
		.filter(st => st.CanBeRequested);

	if (!orderedItemsStates.length) {
		await Message.show(Res.Msg.MSG_NOT_EXISTS_NOTHING_TO_REQUEST());
		return Result.error();
	}

	if (orderedItemsStates.length === 1) {
		const state = orderedItemsStates[0];
		if (state.HasBeenRequested && !await confirmRequestAgain(state)) {
			return Result.error();
		}

		return Result.ok(state.PreparationOrderId);
	}

	return Modal.show(GetPreparationOrder, { status: orderedItemsStates });
}

async function requestCourses(ticketIds: number[]): Promise<Result> {

	const preparationOrderResult = await getOrder(ticketIds);

	if (!preparationOrderResult.ok)
		return Result.error();

	await SalesStore.requestCourses(ticketIds, preparationOrderResult.value);
	return Result.ok();
}


declare global {
	interface CommandType {
		requestCurrentCourses: () => Promise<Result>
		requestCourses: (ticketIds: number[]) => Promise<Result>
	}
}

Command.register('requestCurrentCourses', Preconditions.ensure(Permission.Ticket_RequestOrderedItems), async (): Promise<Result> => {
	const ticket = SalesStore.getTicket();
	if (ticket.IsEmpty) {
		await Message.show(Res.Msg.MSG_TICKET_IS_EMPTY());
		return Result.error();
	}

	return requestCourses([ticket.Id]);
});

Command.register('requestCourses', Preconditions.ensure(Permission.Ticket_RequestOrderedItems), async (ticketIds: number[]): Promise<Result> => {
	return requestCourses(ticketIds);
});
